import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  UserPlus, Users, ArrowLeft, Shield, Truck, ShoppingCart, Eye, EyeOff,
} from "lucide-react";

const ROLES = [
  { value: "founder", label: "Founder", desc: "Full system access", icon: Shield, color: "text-purple-600 bg-purple-50" },
  { value: "ordering", label: "Ordering", desc: "Ordering Portal access only", icon: ShoppingCart, color: "text-amber-600 bg-amber-50" },
  { value: "driver", label: "Driver", desc: "Driver Portal access only", icon: Truck, color: "text-sky-600 bg-sky-50" },
];

export default function ManageAccounts() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ email: "", full_name: "", password: "", role: "ordering" });

  useEffect(() => {
    if (!token) { navigate("/"); return; }
    fetchAccounts();
  }, [token]);

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`${API}/auth/team-accounts`, { headers: { Authorization: `Bearer ${token}` } });
      setAccounts(res.data.accounts || []);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Founders access required");
        navigate("/founders");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.email || !form.full_name || !form.password || !form.role) {
      toast.error("All fields are required");
      return;
    }
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/auth/create-account`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${form.role.charAt(0).toUpperCase() + form.role.slice(1)} account created`);
      setForm({ email: "", full_name: "", password: "", role: "ordering" });
      fetchAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create account");
    } finally {
      setCreating(false);
    }
  };

  const roleInfo = (role) => ROLES.find((r) => r.value === role) || ROLES[1];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="manage-accounts-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/founders" className="text-slate-400 hover:text-slate-600">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-lg font-bold text-slate-900 font-['Outfit']">Manage Accounts</h1>
              <p className="text-xs text-slate-500">Create and view team accounts</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Create Account Form */}
        <div className="bg-white rounded-xl border p-6">
          <div className="flex items-center gap-2 mb-4">
            <UserPlus className="w-5 h-5 text-amber-600" />
            <h2 className="text-base font-semibold text-slate-900">Create New Account</h2>
          </div>

          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                <Input
                  placeholder="John Smith"
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  data-testid="account-fullname"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <Input
                  type="email"
                  placeholder="user@bundlecart.au"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="account-email"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Min 8 characters"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    data-testid="account-password"
                  />
                  <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                <select
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  data-testid="account-role-select"
                >
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label} — {r.desc}</option>
                  ))}
                </select>
              </div>
            </div>

            <Button type="submit" disabled={creating} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="create-account-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              {creating ? "Creating..." : "Create Account"}
            </Button>
          </form>
        </div>

        {/* Existing Accounts */}
        <div className="bg-white rounded-xl border p-6">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-slate-600" />
            <h2 className="text-base font-semibold text-slate-900">Team Accounts ({accounts.length})</h2>
          </div>

          {loading ? (
            <p className="text-slate-500 text-sm py-4">Loading...</p>
          ) : accounts.length === 0 ? (
            <p className="text-slate-500 text-sm py-4">No team accounts created yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">Name</th>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">Email</th>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">Role</th>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {accounts.map((acc) => {
                    const r = roleInfo(acc.role);
                    const Icon = r.icon;
                    return (
                      <tr key={acc.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-900">{acc.full_name}</td>
                        <td className="px-4 py-3 text-slate-600">{acc.email}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${r.color}`}>
                            <Icon className="w-3 h-3" /> {r.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-500 text-xs">{acc.created_at?.slice(0, 10)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
